import React, { useEffect, useState, useContext } from "react";
import { TrafficContext } from "../../App";
import '../../universal.css';
import { NOTAMS_URL, orderedAirports } from '../../data/config';

const NOTAM = () => {
  const [notams, setNotams] = useState([]);
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('metarMode') || "SELECT";
  }); // AUTO / ALL / SELECT / NAV
  const [selectedAirport, setSelectedAirport] = useState(() => {
    return localStorage.getItem('selectedMetarAirport') || "EFHK";
  });
  const [isVisible, setIsVisible] = useState(true); 
  const traffic = useContext(TrafficContext);

  // Fetch NOTAMs function
  const fetchNOTAMs = async () => {
    try {
      const response = await fetch(NOTAMS_URL);
      const data = await response.json();
      let filteredNotams = [];

      if (mode === "AUTO") {
        // Get all relevant ICAOs from traffic (both departure and arrival)
        const icaoSet = new Set(
          traffic
            .map((pilot) => [pilot.flight_plan.departure, pilot.flight_plan.arrival])
            .flat()
            .filter(icao => icao && icao.startsWith("EF")) // Only EF-prefixed airports
        );
      
        // Filter NOTAMs based on the ICAO codes
        filteredNotams = Object.entries(data.notams)
          .filter(([icao]) => icaoSet.has(icao))
          .flatMap(([icao, notamList]) =>
            notamList.map((notam) => ({ ...notam, icao }))
          );
      } else if (mode === "SELECT") {
        filteredNotams = data.notams[selectedAirport] || [];
      } else if (mode === "NAV") {
        filteredNotams = data.notams["extra"] || [];
      } else if (mode === "ALL") {
        filteredNotams = Object.entries(data.notams).flatMap(([icao, notamList]) =>
          notamList.map((notam) => ({ ...notam, icao })) // Assign the ICAO property
        );
      }

      // console.log("Fetched NOTAMs:", filteredNotams);
      setNotams(filteredNotams);
    } catch (error) {
      console.error("Error fetching NOTAMs:", error);
    }
  };

  // Effect to handle NOTAM fetching and interval
  useEffect(() => {
    let interval;

    if (isVisible && mode !== "NONE") {
      fetchNOTAMs(); // Initial fetch
      interval = setInterval(fetchNOTAMs, 5 * 60 * 1000); // Refresh every 5 minutes
    }

    // Cleanup on unmount or when visibility changes
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [mode, selectedAirport, isVisible]);

  useEffect(() => {
    localStorage.setItem('metarMode', mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('selectedMetarAirport', selectedAirport);
  }, [selectedAirport]);

  useEffect(() => {
    if (mode === "AUTO" && isVisible) {
      fetchNOTAMs(); 
    }
  }, [traffic]); 

  const formatNOTAMText = (text) => {

    const cleanedText = text.startsWith("+") ? text.slice(1).trim() : text;
    const fromIndex = cleanedText.indexOf(" FROM:");

    if (fromIndex !== -1) {
      const beforeFrom = cleanedText.slice(0, fromIndex);
      const afterFrom = cleanedText.slice(fromIndex + 1);

      const fromPart = afterFrom.split("TO:")[0];
      const toPart = afterFrom.split("TO:")[1];

      return (
        <>
          <p className="mb-1">{beforeFrom}</p>
          <span className="timestamp light-font">
            <span className="highlight-notam">FROM:</span> {fromPart.replace("FROM:", "").trim()}
            <span className="highlight-notam"> TO:</span> {toPart ? toPart.trim() : ''}
          </span>
        </>
      );
    }

    return cleanedText;
  };

  // Function to group NOTAMs by ICAO code
  const groupNotamsByICAO = (notams) => {
    return notams.reduce((acc, notam) => {
      const icao = notam.icao;
      if (!acc[icao]) {
        acc[icao] = [];
      }
      acc[icao].push(notam);
      return acc;
    }, {});
  };

  // Get grouped NOTAMs
  const groupedNotams = mode === "ALL" || mode === "AUTO" ? groupNotamsByICAO(notams) : {};

  return (
    <div className="text-white h-full">
      <div className="flex space-x-2 bg-gray-300 w-full text-xs">
        <button className={`p-1 ${mode === "AUTO" ? "bg-gray-600" : "bg-gray-400"}`} onClick={() => setMode("AUTO")}>
          AUTO
        </button>
        <button className={`p-1 ${mode === "ALL" ? "bg-gray-600" : "bg-gray-400"}`} onClick={() => setMode("ALL")}>
          ALL
        </button>
        <button className={`p-1 ${mode === "SELECT" ? "bg-gray-600" : "bg-gray-400"}`} onClick={() => setMode("SELECT")}>
          SELECT
        </button>
        <select
          className={`p-1 ${mode === "SELECT" ? "bg-gray-600" : "bg-gray-400"}`}
          onChange={(e) => setSelectedAirport(e.target.value)}
          value={selectedAirport}
          disabled={mode !== "SELECT"}
        >
          {orderedAirports.map((airport) => (
            <option key={airport.icao} value={airport.icao}>
              {airport.icao}
            </option>
          ))}
        </select>
        <button className={`p-1 ${mode === "NAV" ? "bg-gray-600" : "bg-gray-400"}`} onClick={() => setMode("NAV")}>
          ENR
        </button>
      </div>
      <div className="overflow-y-auto text-black text-xs monospace-font mt-1 pb-8 h-full">
        {notams.length === 0 ? (
          <p>No NOTAM data available</p>
        ) : mode === "ALL" ? (
          orderedAirports.map((airport) => {
            const airportNotams = groupedNotams[airport.icao] || [];
            return (
              <div key={airport.icao}>
                <h3 className="small-title">{airport.icao} - {airport.name}</h3>
                {airportNotams.length > 0 ? (
                  <ul className="list-none pl-0">
                    {airportNotams.map((notam, index) => (
                      <li key={index} className="table-line p-1">
                        {formatNOTAMText(notam.text)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>NIL</p>
                )}
              </div>
            );
          })
        ) : mode === "AUTO" ? (
          // Only display airports with NOTAMs in AUTO mode
          Object.entries(groupedNotams).map(([icao, notamList]) => {
            const airport = orderedAirports.find((ap) => ap.icao === icao);
            return (
              <div key={icao}>
                <h3 className="small-title">{icao} - {airport ? airport.name : "Unknown Airport"}</h3>
                <ul className="list-none pl-0">
                  {notamList.map((notam, index) => (
                    <li key={index} className="table-line p-1">
                      {formatNOTAMText(notam.text)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        ) : mode === "NAV" ? (
          <div>
            <h3 className="small-title">NAV WRNG / EN-ROUTE</h3>
            <ul className="list-none pl-0">
              {notams.map((notam, index) => (
                <li key={index} className="table-line p-1">
                  {formatNOTAMText(notam.text)}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          // SELECT
          <div>
            <h3 className="small-title">SELECTED {selectedAirport}</h3>
            <ul className="list-none pl-0">
              {notams.map((notam, index) => (
                <li key={index} className="table-line p-1">
                  {formatNOTAMText(notam.text)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default NOTAM;